import Joyride from 'react-joyride';
import { MenuFoldOutlined } from '@ant-design/icons';
import { Divider, Drawer, Layout, Popover, Typography } from 'antd';
import { getDatabase, onDisconnect, ref, update } from 'firebase/database';
import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { useAuth } from 'hooks/useAuth';
import { logout, updateJustLoggedIn } from '../../../modules/auth/authSlice';
import { selectPageLoader } from 'redux/appSlice';
import PageOverlayLoader from '../../page-overlay-loader/page-overlay-loader.component';
import messagesSvg from 'assets/images/chat/messages.svg';
import notifySvg from 'assets/images/dashboard/notify.svg';
import userSvg from 'assets/images/dashboard/user.svg';
import logoutSvg from 'assets/images/dashboard/logout.svg';
import { useQuery } from 'hooks/useQuery';
import './dashboard-layout.styles.less';
import { DashboardMenu, MENU_DURATION } from './dashboard-menu';
import NiceModal from '@ebay/nice-modal-react';
import { OnboardingState, useOnboardingState } from './onboarding/onboarding';
import { TeamVideoModal } from './onboarding/team-video-modal/team-video-modal.component';
import { joyRideCommonStyles } from './onboarding/joyRideStyles';
import { AddToBookmarksModal } from '../../../modules/gdp/dashboard/components/add-to-bookmarks/add-to-bookmarks-modal';
import { shouldShowBookmarkUsModal } from '../../../modules/gdp/dashboard/components/add-to-bookmarks/utils';
import { PatientSearch } from './patient-search/patient-search';
import { useWhatIsNew } from 'hooks/useWhatIsNew';
import { ZINDEX } from 'utils/ui';
import { PrimaryNotification } from './primary-notification/primary-notification';
import { usePrimaryNotification } from 'hooks/use-primary-notification';
import { useWindowSize } from 'hooks/useWindowSize';
import { usePendo } from 'hooks/usePendo';
import { useAppVersionChecking } from 'hooks/useAppVersionChecking';

const { Header, Content } = Layout;

const DashboardLayout: FC = ({ children }) => {
  const { user, justLoggedIn } = useAuth();
  const { isMd } = useWindowSize();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const queryParams = useQuery();
  const display = queryParams.get('display');
  const open = queryParams.get('open');

  const showOnboardingVideo = open === 'onboardingVideo';
  const isOnboarding = open === 'onboarding' || display === 'onboarding';

  const wizardQuery = queryParams.get('wizard');
  const isWizardSubmittedModalShown = wizardQuery === 'caseSubmitted' || wizardQuery === 'proposalSubmitted';

  const showAppPageLoader = useAppSelector(selectPageLoader);
  const {
    notifications,
    unreadNotificationsCount,
    markNotificationsAs,
    isLoaded: isNotificationLoaded,
  } = usePrimaryNotification();

  const [activeMenu, setActiveMenu] = useState<string>(user?.type || 'dentist');
  const [collapseMenu, setCollapseMenu] = useState(!!localStorage.getItem('collapseMenu'));
  const [showDrawer, setShowDrawer] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const showBookmarkSuggestionModal = shouldShowBookmarkUsModal();

  /* ONBOARDING RELATED HOOKS */
  const onboardingState = useOnboardingState();
  const [isOnboardingContentVisible, setIsOnboardingContentVisible] = useState(false);
  const [justWatchedVideo, setJustWatchedVideo] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [hilightWizardPopover, setHilightWizardPopover] = useState(false);
  /* END OF ONBOARDING RELATED HOOKS */

  usePendo(user);
  useAppVersionChecking();

  useEffect(() => {
    const getActiveMenu = () => {
      const path = location.pathname;
      if (path.includes('patients')) {
        return 'patients';
      }
      if (path.includes('education')) {
        return 'education';
      }
      if (path.includes('orders')) {
        return 'orders';
      }
      if (path.includes('pricing')) {
        return 'pricing';
      }
      return user?.type || 'dentist';
    };

    const actMenu = getActiveMenu();
    setActiveMenu(actMenu);
    setShowDrawer(false);
  }, [location, user]);

  const pageAccessedByReload =
    (window.performance.navigation && window.performance.navigation.type === 1) ||
    window.performance
      .getEntriesByType('navigation')
      .map((nav: any) => nav?.type)
      .includes('reload');

  useEffect(() => {
    if (display && display === 'onboarding') {
      setIsOnboardingContentVisible(true);
      setTimeout(() => {
        setShowOnboardingWizard(true);
      }, 400);
    }
  }, [display, pageAccessedByReload]);

  useEffect(() => {
    if (!isOnboardingContentVisible) {
      setHilightWizardPopover(false);
    }

    if (!isOnboardingContentVisible && justWatchedVideo && !isOnboarding && !isWizardSubmittedModalShown) {
      setJustWatchedVideo(false);
    }
  }, [isOnboardingContentVisible, justWatchedVideo, isOnboarding, isWizardSubmittedModalShown]);

  useEffect(() => {
    if (showOnboardingVideo) {
      NiceModal.show(TeamVideoModal, {
        onClose: () => {
          history.push(location.pathname);
        },
      });
    }
  }, [showOnboardingVideo]);

  const isWhatIsNewShown = useWhatIsNew();

  useEffect(() => {
    if ((!user?.newRelease || !isWhatIsNewShown) && isNotificationLoaded) {
      // wait for new release popup is shown first (if any) AND notification loaded
      const showPopup = async () => {
        // show bookmark popup
        if (user?.status === 'live' && onboardingState === OnboardingState.FINISHED && showBookmarkSuggestionModal) {
          await NiceModal.show(AddToBookmarksModal);
        }
        // show notification bar
        if (justLoggedIn) {
          if (unreadNotificationsCount) setShowNotifications(true);
          dispatch(updateJustLoggedIn({ justLoggedIn: false }));
        }
      };
      showPopup();
    }
  }, [isWhatIsNewShown, showBookmarkSuggestionModal, justLoggedIn, unreadNotificationsCount, isNotificationLoaded]);

  const toggleSideDrwer = () => {
    setShowDrawer(!showDrawer);
  };

  const onClose = () => {
    setShowDrawer(false);
  };

  const logoutUser = () => {
    dispatch(logout());
    history.push('');
    const database = getDatabase();
    const userRef = ref(database, `users/${user?.id}`);
    update(userRef, {
      isOnline: false,
    })
      .then(() => {
        onDisconnect(userRef).update({ isOnline: false });
      })
      .catch((err: any) => {
        // TODO: need to error handling
        console.error('failed to update a data', err);
      });
  };

  const userProfile = () => {
    history.push(`/${user?.type}/profile`);
  };

  const onboardingSteps = [
    {
      target: '.start-onboarding',
      title: 'Your Journey Tutorial',
      content: (
        <div className="text-sm font-normal gray-color">
          In case you want to restart your journey again? You can start from here!
        </div>
      ),
      disableBeacon: true,
      spotlightPadding: 0,
    },
  ];

  const handleJoyrideCallback = (data: any) => {
    if (data.status === 'finished') {
      setIsOnboardingContentVisible(false);
      setShowOnboardingWizard(false);
      history.push(`/${user?.type}`);

      setJustWatchedVideo(false);
    }
  };

  const helpPopoverContent = (
    <div className="w-15rem p-2">
      <Typography.Text className="font-bold text-sm">Help and Support</Typography.Text>
      <Divider className="mt-1 mb-2" />
      <div className="flex">
        <div className="grow mr-1 flex-row text-justify">
          <Typography.Text className="text-xs line-height-1" style={{ color: '#6b7d8b' }}>
            If you have a question about a patient click into their profile and use the <strong>chat tool!</strong> Look
            for this icon bottom right
          </Typography.Text>
          <div className="mt-2">
            <Typography.Text style={{ color: '#6b7d8b' }} className="text-xs line-height-1">
              For anything else please contact <br />{' '}
              <a href="mailto:support@32co.com" style={{ color: '#6b7d8b', fontWeight: 700 }}>
                support@32co.com
              </a>
            </Typography.Text>
          </div>
        </div>
        <div className="flex flex-none mt-3 ml-3">
          <div className="flex justify-content-center align-items-center cursor-pointer chat-icon-container">
            <img alt="" src={messagesSvg} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout className="layout">
      <Joyride
        steps={onboardingSteps}
        continuous
        run={showOnboardingWizard}
        showSkipButton={false}
        disableScrolling
        disableCloseOnEsc
        disableOverlayClose
        showProgress={false}
        locale={{ back: 'Back', close: 'Close', last: 'Close', next: 'Next', skip: 'Skip' }}
        styles={{
          options: {
            zIndex: ZINDEX.JOYRIDE,
          },
          ...joyRideCommonStyles,
        }}
        callback={handleJoyrideCallback}
      />
      <div
        className="custom-menu-s hidden md:block"
        style={{
          width: collapseMenu ? '100px' : '200px',
          transition: `width ${MENU_DURATION}`,
        }}
      >
        <DashboardMenu
          user={user!}
          activeMenu={activeMenu}
          showOnboardingWizard={showOnboardingWizard}
          onSetShowOnboardingWizard={setShowOnboardingWizard}
          isOnboardingContentVisible={isOnboardingContentVisible}
          onSetIsOnboardingContentVisible={setIsOnboardingContentVisible}
          hilightWizardPopover={hilightWizardPopover}
          smallMode={collapseMenu}
        />
      </div>

      <Drawer
        className="md:hidden"
        placement="left"
        onClose={onClose}
        visible={showDrawer}
        bodyStyle={{ padding: 0 }}
        contentWrapperStyle={{
          width: '200px',
          background: '#060A43',
        }}
        closable={false}
      >
        <div className="custom-menu-s-mobile">
          <DashboardMenu
            user={user!}
            activeMenu={activeMenu}
            showOnboardingWizard={showOnboardingWizard}
            onSetShowOnboardingWizard={setShowOnboardingWizard}
            isOnboardingContentVisible={isOnboardingContentVisible}
            onSetIsOnboardingContentVisible={setIsOnboardingContentVisible}
            hilightWizardPopover={hilightWizardPopover}
            smallMode={false}
          />
        </div>
      </Drawer>

      <PrimaryNotification
        showNotifications={showNotifications}
        onSetShowNotifications={setShowNotifications}
        notifications={notifications}
        unreadNotificationsCount={unreadNotificationsCount}
        markNotificationsAs={markNotificationsAs}
      />

      <Layout
        className="site-layout"
        style={{
          marginLeft: isMd ? (collapseMenu ? '100px' : '200px') : '0px',
          transition: `margin-left ${MENU_DURATION}`,
        }}
      >
        <Header
          id="MAIN_HEADER_ID"
          className="app-header bg-white fixed"
          style={{
            width: isMd ? (collapseMenu ? 'calc(100% - 100px)' : 'calc(100% - 200px)') : '100%',
            zIndex: isOnboarding ? 1 : ZINDEX.LAYOUT_HEADER,
          }}
        >
          <div className="flex justify-content-between px-3">
            <div className="md:hidden cursor-pointer" onClick={toggleSideDrwer}>
              <MenuFoldOutlined />
              <span className="hidden ml-2">Show menu</span>
            </div>

            <div
              className="hidden md:block cursor-pointer"
              onClick={() => {
                localStorage.setItem('collapseMenu', !collapseMenu ? 'true' : '');
                setCollapseMenu(!collapseMenu);
              }}
            >
              <MenuFoldOutlined />
              <span className="ml-2">{collapseMenu ? 'Expand' : 'Collapse'} menu</span>
            </div>

            <div>
              <div className="flex align-items-center">
                {user && user?.type === 'dentist' && (
                  <div>
                    <PatientSearch />
                  </div>
                )}
                <Popover
                  content={helpPopoverContent}
                  title=""
                  placement="bottomRight"
                  trigger="click"
                  className="cursor-pointer"
                >
                  <span className="text-sm ml-5">Help</span>
                </Popover>

                <div className="ml-5 relative cursor-pointer">
                  <div onClick={() => setShowNotifications(true)}>
                    <img src={notifySvg} alt="notifySvg" />
                  </div>
                  {unreadNotificationsCount > 0 && (
                    <span
                      className="absolute"
                      style={{
                        background: 'rgb(216, 66, 94)',
                        height: '6px',
                        width: '6px',
                        borderRadius: '50%',
                        top: '50%',
                        right: '-6px',
                        transform: 'translate(-10px, -11px)',
                      }}
                    />
                  )}
                </div>
                <div onClick={userProfile} className="ml-5 cursor-pointer">
                  <img src={userSvg} alt="userProfile" />
                </div>
                <div onClick={logoutUser} className="ml-5 cursor-pointer">
                  <img src={logoutSvg} alt="logoutUser" />
                </div>
              </div>
            </div>
          </div>
        </Header>
        <Content className="content-section mx-3">
          {children}
          <PageOverlayLoader loading={showAppPageLoader} />
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
